import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { bankingAPI, companyAPI } from '../../services/api';
import toast from 'react-hot-toast';

/** Format a value in an explicit currency code (falls back to a plain label). */
function fmtCur(value, code) {
  const n = Number(value) || 0;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(n);
  } catch (e) {
    return `${code} ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export default function BankTransactions() {
  const [searchParams] = useSearchParams();
  const [txns, setTxns] = useState([]); const [loading, setLoading] = useState(true); const [bankAccountId, setBankAccountId] = useState(''); const [accounts, setAccounts] = useState([]); const [baseCurrency, setBaseCurrency] = useState('USD');
  const [showTransfer, setShowTransfer] = useState(false); const [transferForm, setTransferForm] = useState({from_bank_account_id:'',to_bank_account_id:'',amount:0,transfer_date:new Date().toISOString().split('T')[0],memo:''});
  // Edit/delete a mistakenly entered transaction
  const [editTxn, setEditTxn] = useState(null);
  const [editForm, setEditForm] = useState({});
  const loadTxns = () => bankingAPI.transactions({bankAccountId:bankAccountId||undefined}).then(setTxns).catch(e=>toast.error(e.message)).finally(()=>setLoading(false));

  useEffect(() => {
    bankingAPI.accounts()
      .then(setAccounts)
      .catch(e => toast.error(e.message || 'Could not load bank accounts'));
    companyAPI.current().then(c => { if (c && c.base_currency) setBaseCurrency(c.base_currency); }).catch(() => {});
  }, []);
  useEffect(() => { bankingAPI.transactions({bankAccountId:bankAccountId||undefined}).then(setTxns).catch(e=>toast.error(e.message)).finally(()=>setLoading(false)); }, [bankAccountId]);
  // Allow deep-linking straight into the transfer dialog (/banking/transactions?transfer=1)
  useEffect(() => { if (searchParams.get('transfer')) setShowTransfer(true); }, [searchParams]);

  const handleTransfer = async(e) => { e.preventDefault();
    if (!transferForm.from_bank_account_id || !transferForm.to_bank_account_id) { toast.error('Select both accounts'); return; }
    if (transferForm.from_bank_account_id === transferForm.to_bank_account_id) { toast.error('From and To accounts must be different'); return; }
    if (!(parseFloat(transferForm.amount) > 0)) { toast.error('Enter an amount'); return; }
    try { await bankingAPI.createTransfer(transferForm); toast.success('Transfer completed'); setShowTransfer(false); setTransferForm({...transferForm, from_bank_account_id:'',to_bank_account_id:'',amount:0,memo:''}); bankingAPI.transactions({bankAccountId:bankAccountId||undefined}).then(setTxns); } catch(err){toast.error(err.message);} };

  const canTransfer = accounts.length >= 2;
  const doDelete = async (t) => {
    if (!window.confirm('Delete this bank transaction? This also removes its journal entry and restores the account balance.')) return;
    try {
      await bankingAPI.deleteTransaction(t.id);
      toast.success('Transaction deleted');
      loadTxns();
      bankingAPI.accounts().then(setAccounts).catch(() => {});
    } catch (err) {
      toast.error(err.message || 'Could not delete transaction');
    }
  };
  return (
    <div>
      <div className="toolbar"><h2>Bank Transactions</h2>
        <div className="toolbar-right">
          <button className="btn btn-primary" onClick={()=>setShowTransfer(true)}>Transfer</button>
        </div>
      </div>
      <div className="filters-bar">
        <select className="form-control" value={bankAccountId} onChange={e=>setBankAccountId(e.target.value)}>
          <option value="">All Accounts</option>{accounts.map(a=><option key={a.id} value={a.id}>{a.bank_name || a.account_name}</option>)}
        </select>
      </div>
      {loading ? <div className="spinner" style={{margin:'40px auto'}} /> : (
        <div className="card"><div className="card-body" style={{padding:0}}>
          <table>
            <thead><tr><th>Date</th><th>Description</th><th>Reference</th><th className="text-right">Deposit</th><th className="text-right">Withdrawal</th><th className="text-right">Balance</th><th>Reconciled</th><th></th></tr></thead>
            <tbody>
              {txns.map(t => {
                const cur2 = t.account_currency ? String(t.account_currency).toUpperCase() : baseCurrency;
                return (<tr key={t.id}>
                  <td>{t.transaction_date}</td><td>{t.description}</td><td className="text-muted">{t.reference||t.check_number||'-'}</td>
                  <td className="amount">{t.deposit > 0 ? fmtCur(t.deposit, cur2) : ''}</td><td className="amount">{t.withdrawal > 0 ? fmtCur(t.withdrawal, cur2) : ''}</td>
                  <td className="amount font-bold">{fmtCur(t.balance, cur2)}</td><td>{t.is_reconciled ? '✅' : '⬜'}</td>
                  <td><button className="btn btn-ghost btn-sm" title="Delete" onClick={() => doDelete(t)} style={{ color: '#dc2626' }}>✕</button></td>
                </tr>);
              })}
              {txns.length === 0 && <tr><td colSpan={8} className="text-center text-muted" style={{padding:30}}>No transactions found</td></tr>}
            </tbody>
          </table>
        </div></div>
      )}
      {showTransfer && (<div className="modal-overlay" onClick={()=>setShowTransfer(false)}><div className="modal" onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><h3>Transfer Funds</h3><button className="btn-ghost" onClick={()=>setShowTransfer(false)}>✕</button></div>
        <form onSubmit={handleTransfer}><div className="modal-body">
          {!canTransfer && (
            <p className="badge badge-warning" style={{display:'block',padding:'10px 12px',marginBottom:12,whiteSpace:'normal'}}>
              You need at least two bank accounts to make a transfer.
              Add another bank account first (Banking → Add Bank Account).
            </p>
          )}
          <div className="form-grid">
            <div className="form-group"><label>From Account</label><select className="form-control" required disabled={!canTransfer} value={transferForm.from_bank_account_id} onChange={e=>setTransferForm({...transferForm,from_bank_account_id:e.target.value})}>
              <option value="">Select...</option>{accounts.map(a=><option key={a.id} value={a.id}>{a.bank_name||a.account_name}{a.bank_balance!=null?` (${fmtCur(a.bank_balance, a.currency ? String(a.currency).toUpperCase() : baseCurrency)})`:''}</option>)}</select></div>
            <div className="form-group"><label>To Account</label><select className="form-control" required disabled={!canTransfer} value={transferForm.to_bank_account_id} onChange={e=>setTransferForm({...transferForm,to_bank_account_id:e.target.value})}>
              <option value="">Select...</option>{accounts.filter(a=>String(a.id)!==String(transferForm.from_bank_account_id)).map(a=><option key={a.id} value={a.id}>{a.bank_name||a.account_name}{a.bank_balance!=null?` (${fmtCur(a.bank_balance, a.currency ? String(a.currency).toUpperCase() : baseCurrency)})`:''}</option>)}</select></div>
            <div className="form-group"><label>Amount</label><input className="form-control" type="number" step="0.01" min="0.01" required disabled={!canTransfer} value={transferForm.amount||''} onChange={e=>setTransferForm({...transferForm,amount:parseFloat(e.target.value)||0})}/></div>
            <div className="form-group"><label>Date</label><input className="form-control" type="date" required disabled={!canTransfer} value={transferForm.transfer_date} onChange={e=>setTransferForm({...transferForm,transfer_date:e.target.value})}/></div>
            <div className="form-group full-width"><label>Memo</label><input className="form-control" disabled={!canTransfer} value={transferForm.memo||''} onChange={e=>setTransferForm({...transferForm,memo:e.target.value})}/></div>
          </div>
        </div>
        <div className="modal-footer"><button type="button" className="btn btn-outline" onClick={()=>setShowTransfer(false)}>Cancel</button><button type="submit" className="btn btn-primary" disabled={!canTransfer}>Transfer</button></div>
        </form>
      </div></div>)}
    </div>
  );
}
